"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import {
  POSTHOG_TOKEN,
  POSTHOG_HOST,
  POSTHOG_UI_HOST,
} from "@/lib/posthog-config";
import { getConsent } from "@/lib/consent";
import { isProductionClient } from "@/lib/site-env";

/**
 * Client-side PostHog boot.
 *
 * This file exists because the monorepo never had one: the old standalone
 * site repo initialized posthog-js in the browser, but that component did not
 * survive the move to apps/site. Server-side capture kept working, so nothing
 * errored — the browser half of analytics (pageviews, autocapture, web
 * vitals, session replay) just silently stopped the day the Railway service
 * started building from this repo. Every component that calls
 * `posthog.capture(...)` has been talking to an uninitialized SDK since.
 *
 * Consent: the SDK starts opted OUT. ConsentBanner owns the choice — it calls
 * `opt_in_capturing()` / `opt_out_capturing()` live on Accept/Reject — and
 * this component reconciles the stored choice on every load, so a returning
 * visitor who accepted keeps capturing without seeing the banner again. The
 * has_opted_* guards keep that reconciliation from re-firing an `$opt_in`
 * event on every pageview.
 *
 * Host gating matches the other trackers in layout.tsx: the token is
 * publishable and hardcoded, so the only thing keeping a dev deploy out of
 * production analytics is refusing to init anywhere but the real domain.
 */

let initialized = false;

export default function PostHogTracker() {
  useEffect(() => {
    if (initialized || !isProductionClient()) return;
    initialized = true;

    posthog.init(POSTHOG_TOKEN, {
      // First-party managed proxy — keeps ad blockers from dropping events.
      api_host: POSTHOG_HOST,
      // Toolbar / in-app links resolve to the real dashboard, not the proxy.
      ui_host: POSTHOG_UI_HOST,

      // "2025-05-24" defaults include capture_pageview: "history_change",
      // which fires $pageview on App Router client navigations — no manual
      // route-change effect to keep in sync.
      defaults: "2025-05-24",

      // Everything below denied-by-default; ConsentBanner flips it.
      opt_out_capturing_by_default: true,

      // Match what the old deployed site captured: exceptions and rage
      // clicks came through as $exception / $rageclick events, and Sentry's
      // config deliberately leaves session replay to PostHog.
      capture_exceptions: true,
      rageclick: true,

      persistence: "localStorage+cookie",
      person_profiles: "identified_only",
    });

    // Reconcile the SDK's own persisted opt state with our consent store —
    // aa_consent is the source of truth, the SDK flag is just a mirror.
    const consent = getConsent();
    if (consent === "granted" && !posthog.has_opted_in_capturing()) {
      posthog.opt_in_capturing();
    } else if (consent === "denied" && !posthog.has_opted_out_capturing()) {
      posthog.opt_out_capturing();
    }

    // Shared project across Main / Digital / BIAB — tag the surface so
    // dashboards can split them.
    posthog.register({ site: "main" });
  }, []);

  return null;
}
